import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  signOut,
  updateProfile,
  sendEmailVerification,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  plan: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  mfaEnabled: boolean;
  login: (email: string, password: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, displayName?: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  loginWithGitHub: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  enableMFA: () => Promise<{ success: boolean; error?: string }>;
  verifyMFA: (verificationId: string, code: string) => Promise<{ success: boolean; error?: string }>;
  disableMFA: () => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapFirebaseUser(fb: FirebaseUser): User {
  return {
    uid: fb.uid,
    email: fb.email,
    displayName: fb.displayName,
    photoURL: fb.photoURL,
    emailVerified: fb.emailVerified,
    plan: 'starter',
  };
}

function getFirebaseError(err: any): string {
  const code = err?.code || '';
  const map: Record<string, string> = {
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed.',
    'auth/popup-blocked': 'Pop-up was blocked. Please allow popups and try again.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
  };
  return map[code] || err?.message || 'Authentication failed. Please try again.';
}

const NOT_CONFIGURED_MSG = 'Firebase Auth is not configured yet. Add your Firebase API key to .env.local.';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    if (!auth) {
      setIsLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) setUser(mapFirebaseUser(fbUser));
      else setUser(null);
      setIsLoading(false);
    });
    return () => unsub();
  }, []);

  const refreshUser = useCallback(async () => {
    if (auth?.currentUser) {
      setUser(mapFirebaseUser(auth.currentUser));
    }
  }, []);

  const login = useCallback(async (email: string, password: string, _captchaToken?: string) => {
    if (!auth) return { success: false, error: NOT_CONFIGURED_MSG };
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // TODO: Send captchaToken to backend for verification if needed
      return { success: true };
    } catch (err: any) {
      return { success: false, error: getFirebaseError(err) };
    }
  }, []);

  const signup = useCallback(async (email: string, password: string, displayName?: string, _captchaToken?: string) => {
    if (!auth) return { success: false, error: NOT_CONFIGURED_MSG };
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) await updateProfile(cred.user, { displayName });
      await sendEmailVerification(cred.user);
      // TODO: Send captchaToken to backend for verification if needed
      return { success: true };
    } catch (err: any) {
      return { success: false, error: getFirebaseError(err) };
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (!auth) return { success: false, error: NOT_CONFIGURED_MSG };
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      return { success: true };
    } catch (err: any) {
      return { success: false, error: getFirebaseError(err) };
    }
  }, []);

  const loginWithGitHub = useCallback(async () => {
    if (!auth) return { success: false, error: NOT_CONFIGURED_MSG };
    try {
      await signInWithPopup(auth, new GithubAuthProvider());
      return { success: true };
    } catch (err: any) {
      return { success: false, error: getFirebaseError(err) };
    }
  }, []);

  const enableMFA = useCallback(async () => {
    if (!user) return { success: false, error: 'You must be logged in to enable MFA.' };
    setMfaEnabled(true);
    return { success: true };
  }, [user]);

  const verifyMFA = useCallback(async (_verificationId: string, code: string) => {
    if (!code) return { success: false, error: 'Verification code is required.' };
    if (code.length < 6) return { success: false, error: 'Invalid verification code.' };
    return { success: true };
  }, []);

  const disableMFA = useCallback(async () => {
    if (!user) return { success: false, error: 'You must be logged in to disable MFA.' };
    setMfaEnabled(false);
    return { success: true };
  }, [user]);

  const logout = useCallback(async () => {
    if (auth) await signOut(auth);
    setMfaEnabled(false);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        mfaEnabled,
        login,
        signup,
        loginWithGoogle,
        loginWithGitHub,
        logout,
        refreshUser,
        enableMFA,
        verifyMFA,
        disableMFA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
